/**
 * Hooks do self-service de remessa do parceiro (E21.4.3b, G4 / FR-17 no portal).
 * Escopo restrito ao próprio parceiro (terminais/histórico) — gate no backend.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

import {
  portalCentralApi,
  type PortalGenerateRemessaPayload,
} from "@/services/portalApi";
import { triggerBrowserDownload } from "@/lib/browser-download";

const GEN_KEY = ["portal", "central", "remessa", "generations"];

function detailOf(err: unknown, fallback: string): string {
  const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length && typeof d[0]?.msg === "string") return d[0].msg;
  return fallback;
}

export function usePortalRemessaTemplates() {
  return useQuery({
    queryKey: ["portal", "central", "remessa", "templates"],
    queryFn: () => portalCentralApi.listRemessaTemplates(),
    staleTime: 60000,
  });
}

export function usePortalRemessaTerminals(search: string) {
  return useQuery({
    queryKey: ["portal", "central", "remessa", "terminals", search],
    queryFn: () => portalCentralApi.listRemessaTerminals(search),
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
}

export function usePortalRemessaGenerations(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: [...GEN_KEY, page, pageSize],
    queryFn: () => portalCentralApi.listRemessaGenerations(page, pageSize),
    placeholderData: keepPreviousData,
  });
}

/** Gera a remessa e BAIXA o PDF (presigned attachment). */
export function usePortalGenerateRemessa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: PortalGenerateRemessaPayload;
    }) => {
      const gen = await portalCentralApi.generateRemessa(templateId, payload);
      const info = await portalCentralApi.getRemessaDownload(gen.id);
      triggerBrowserDownload(info.download_url);
      return gen;
    },
    onSuccess: (gen) => {
      qc.invalidateQueries({ queryKey: GEN_KEY });
      toast.success(`Remessa gerada (protocolo ${gen.protocol_number})`);
    },
    onError: (err) => toast.error(detailOf(err, "Não foi possível gerar a remessa.")),
  });
}

/** Re-download de uma remessa do próprio parceiro. */
export function usePortalRemessaDownload() {
  return useMutation({
    mutationFn: async (generationId: string) => {
      const info = await portalCentralApi.getRemessaDownload(generationId);
      triggerBrowserDownload(info.download_url);
    },
    onError: (err) => toast.error(detailOf(err, "Não foi possível baixar o PDF.")),
  });
}
